import { Typography, Tooltip } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import { ApiService } from '../../../../Services/ApiService';
import { IUnitConfiguration } from '../../../../Models/IUnitConfiguration';
import { IDesignSession } from '../../../../Models/IDesignSession';
import { deCapitalizeFirstLetter, getFlowTypeUnits } from '../../../../Services/Global';
import { getFormattedColumnValue } from '../../../../Utils/UnitConfigurationHelper';
import { CellFormatContext, formatCellValue } from '../../../../Utils/CellUnitFormatter';
import { BlindTestListState, ColumnConfig, COLUMN_WIDTHS, ColumnWidth } from './BlindTestListTypes';
import { SourceChip, DateCell } from './BlindTestListComponents';
import logger from '../../../../Services/LoggingService';

/**
 * Get appropriate column width based on column key
 */
export const getColumnWidth = (key: string): ColumnWidth => {
  const lowerKey = key.toLowerCase();
  
  if (lowerKey === 'id' || lowerKey === 'row') return COLUMN_WIDTHS.row;
  if (lowerKey === 'source') return COLUMN_WIDTHS.source;
  if (lowerKey === 'modelid' || lowerKey.includes('model')) return COLUMN_WIDTHS.modelId;
  if (lowerKey === 'mediatype' || lowerKey === 'hotmediatype' || lowerKey.includes('media')) return COLUMN_WIDTHS.mediaType;
  if (lowerKey.includes('date') || lowerKey === 'created' || lowerKey === 'createddate') return COLUMN_WIDTHS.date;
  if (lowerKey.includes('temp')) return COLUMN_WIDTHS.temperature;
  if (lowerKey.includes('flowrate')) return COLUMN_WIDTHS.flowrate;
  if (lowerKey.includes('pressure')) return COLUMN_WIDTHS.pressure;
  if (lowerKey === 'comment') return COLUMN_WIDTHS.comment;
  
  return COLUMN_WIDTHS.default;
};

/**
 * Format model ID consistently
 */
export const formatModelId = (row: any): string => {
  return row.modelId || (row.model?.id || row.model?.modelId) || '-';
};

/**
 * Format a cell value using the shared unit-aware formatter. The formatter
 * delegates numeric conversion to UnitsHelper.formatUnitValue so values coming
 * from the backend (always metric) are converted to the user's preferred units
 * and rendered with the correct label. Empty values are rendered as '-' to
 * match the existing BlindTestList UX.
 */
export const formatBlindTestCellValue = (
  value: any,
  columnKey: string,
  formatContext: CellFormatContext
): string =>
  formatCellValue(columnKey, value, formatContext, { emptyFallback: '-' });

/**
 * Derive a CellFormatContext for the blindTests-prop code path where only the
 * preferred flowrate unit label (e.g. 'kg/h', 'GPM') is available. Mass vs
 * volumetric is inferred from the label so existing callers don't need to
 * thread the raw flowType through props.
 */
const inferFormatContextFromFlowrateUnits = (flowrateUnits: string): CellFormatContext => {
  const normalized = flowrateUnits.trim().toLowerCase();
  if (normalized === 'm3/h' || normalized === 'gpm') {
    return { flowType: 'Volumetric' };
  }
  return { flowType: 'Mass' };
};

/**
 * Create standard base column configuration
 */
export const createBaseColumns = (onItemClick: (item: any) => void): ColumnConfig[] => {
  return [
    {
      id: 'id',
      label: 'Row',
      width: COLUMN_WIDTHS.row,
      align: 'left',
      format: (value: any, row: any) => (
        <Tooltip title={row.id?.toString() || ''} arrow>
          <Typography variant="body2" noWrap>{row.id}</Typography>
        </Tooltip>
      ),
      sortable: true
    },
    {
      id: 'source',
      label: 'Fidelity',
      width: COLUMN_WIDTHS.source,
      align: 'left',
      format: (value: any, row: any) => <SourceChip item={row} onItemClick={onItemClick} />,
      sortable: true
    },
    {
      id: 'testReferenceId',
      label: 'Ref Id',
      width: { minWidth: 75, maxWidth: 75 },
      align: 'left',
      format: (value: any, row: any) => (
        <Tooltip title={row.testReferenceId?.toString() || ''} arrow>
          <Typography variant="body2" noWrap>{row.testReferenceId}</Typography>
        </Tooltip>
      ),
      sortable: true
    },
    {
      id: 'modelId',
      label: 'Model ID',
      width: COLUMN_WIDTHS.modelId,
      align: 'left',
      format: (value: any, row: any) => {
        const modelIdValue = formatModelId(row);
        return (
          <Tooltip title={modelIdValue} arrow>
            <Typography variant="body2" noWrap>{modelIdValue}</Typography>
          </Tooltip>
        );
      },
      sortable: true
    },
    {
      id: 'blindTestDataSetId',
      label: 'Dataset ID',
      width: { minWidth: 80, maxWidth: 80 },
      align: 'left',
      format: (value: any, row: any) => (
        <Tooltip title={row.blindTestDataSetId?.toString() || ''} arrow>
          <Typography variant="body2" noWrap>{row.blindTestDataSetId}</Typography>
        </Tooltip>
      ),
      sortable: true
    }
  ];
};

/**
 * Create columns for blind test data
 */
export const createBlindTestColumns = (
  sampleItem: any, 
  onItemClick: (item: any) => void, 
  preferredFlowrateUnits: string,
  theme: any,
  formatContext?: CellFormatContext
): ColumnConfig[] => {
  const baseColumns = createBaseColumns(onItemClick);

  // Define keys to exclude from dynamic columns
  const excludedKeys = ['id', 'testReferenceId', 'modelId', 'blindTestDataSetId', 'comment'];

  // Callers using the legacy `preferredFlowrateUnits` string argument (blindTests
  // prop path) don't thread the raw flowType. Infer it from the unit label so
  // mass vs volumetric flows are rendered correctly after conversion.
  const effectiveFormatContext: CellFormatContext =
    formatContext ?? inferFormatContextFromFlowrateUnits(preferredFlowrateUnits);

  // Add columns for each property in blindTests
  const dynamicColumns = Object.keys(sampleItem)
    .filter(key => !excludedKeys.includes(key))
    .map(key => ({
      id: key,
      label: key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()),
      width: getColumnWidth(key),
      align: 'left' as const,
      format: (value: any, row: any) => {
        const displayValue = formatBlindTestCellValue(row[key], key, effectiveFormatContext);
        return (
          <Tooltip title={displayValue} arrow>
            <Typography 
              variant="body2" 
              noWrap
              sx={{ 
                color: key.toLowerCase().includes('hot') 
                  ? theme.palette.error.dark 
                  : theme.palette.primary.main 
              }}
            >
              {displayValue}
            </Typography>
          </Tooltip>
        );
      },
      sortable: true
    }));
  
  // Always add comment column at the end for blind tests
  const commentColumn: ColumnConfig = {
    id: 'comment',
    label: 'Comment',
    width: COLUMN_WIDTHS.comment,
    align: 'left',
    format: (value: any, row: any) => row.modelerComment ? (
      <Tooltip title={row.modelerComment || ''} arrow>
        <ChatIcon fontSize="small" />
      </Tooltip>
    ) : null,
    sortable: false
  };
  
  return [...baseColumns, ...dynamicColumns, commentColumn];
};

/**
 * Get design session data from the API
 */
export const createGetDesignSessionData = (
  apiService: ApiService,
  state: BlindTestListState,
  positionSummary: any,
  configurations: IUnitConfiguration[]
) => {
  return async (query?: string, pageNumber?: number, overrideRowsPerPage?: number) => {
    if (!state.unitInputConfiguration || !positionSummary?.blindTestDataSetId) {
      return;
    }

    state.setIsLoading(true);
    const currentPageNum = pageNumber !== undefined ? pageNumber : state.currentPage;
    state.setCurrentPage(currentPageNum);
    
    if (configurations.length > 0) {
      try {
        // Use override value if provided, otherwise use state value
        const effectiveRowsPerPage = overrideRowsPerPage !== undefined ? overrideRowsPerPage : state.rowsPerPage;
        
        // Use a large number for pageSize when "All" is selected or when filtering
        const pageSize = effectiveRowsPerPage === -1 ? 1000 : effectiveRowsPerPage;
        
        // When filtering with a large page size, request all data from server
        const pageOffset = pageSize > 1000 ? 0 : currentPageNum * pageSize;
        
        const response = await apiService.getAsync(
          `designsessions/positions?pageOffset=${pageOffset}&pageSize=${pageSize}${query ?? state.currentFilter}&samplingId=${positionSummary.blindTestDataSetId}&sectionLength=${configurations.length}`, 
          state.unitInputConfiguration
        );
        
        if (!response.ok) {
          throw new Error(`Error: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        const designSessions: IDesignSession[] = data.items;
        
        logger.debug('Design sessions loaded from API', {
          sessionsCount: designSessions.length,
          totalRows: data.totalRows,
          pageOffset,
          pageSize
        }, {
          component: 'BlindTestList',
          method: 'getDesignSessionData'
        });
        
        let sessionGroupedData: any[] = [];

        // Process data based on section configuration
        if (configurations.length === 1) {
          processSingleSectionData(designSessions, sessionGroupedData, configurations);
        } else {
          processMultiSectionData(designSessions, sessionGroupedData, configurations);
        }

        const totalRows: number = data.totalRows / configurations.length;
        
        // Handle pagination based on page size
        if (pageSize > 1000) {
          // Store full filtered data
          state.setCurrentData(sessionGroupedData);
          // Set page count based on actual filtered data
          state.setPageCount(Math.ceil(sessionGroupedData.length / (state.rowsPerPage === -1 ? sessionGroupedData.length : state.rowsPerPage)));
        } else {
          // Normal case - using server pagination
          state.setPageCount(state.rowsPerPage === -1 ? 1 : Math.ceil(totalRows / pageSize));
          state.setCurrentData(sessionGroupedData);
        }
      } catch (error) {
        logger.error('Failed to load design session data', error as Error, {
          query: query ?? state.currentFilter,
          pageNumber: currentPageNum,
          rowsPerPage: overrideRowsPerPage !== undefined ? overrideRowsPerPage : state.rowsPerPage,
          blindTestDataSetId: positionSummary?.blindTestDataSetId
        }, {
          component: 'BlindTestList',
          method: 'getDesignSessionData'
        });
      } finally {
        state.setIsLoading(false);
      }
    }  
  };
};

/**
 * Process multi-section data
 */
export const processMultiSectionData = (
  designSessions: IDesignSession[], 
  sessionGroupedData: any[], 
  configurations: IUnitConfiguration[]
) => {
  let groupedData: { [key: string]: IDesignSession[] } = {};

  // Group by sectionId
  designSessions.forEach((item: IDesignSession) => {
    if (!item.sectionId) return;
    
    if (!groupedData[item.sectionId]) {
      groupedData[item.sectionId] = [];
    }
    
    groupedData[item.sectionId].push(item);
  });

  // Process each group
  Object.keys(groupedData).forEach((key: string) => {
    const values = groupedData[key];
    values.sort((a, b) => (a.sectionNumber || 0) - (b.sectionNumber || 0));
    
    let concatenatedSource = values.map(value => value.source).join(' | ');
    if (concatenatedSource.split(' | ').every(value => value.trim() === 'Unassigned')) {
      concatenatedSource = 'Unassigned';
    }

    let foundMinValue = false;
    for (let i = 1; i <= configurations.length; i++) {
      const value: any = values.find(c => c.sectionNumber === i);
      const inputProps = configurations
        .filter(c => c.sectionNumber === i)
        .map(c => c.inputs)
        .flat();

      if (value !== undefined) {
        if (foundMinValue === false) {
          // Create a new grouped item
          foundMinValue = true;
          const clonedValue = { ...value };
          
          // Add section-specific properties
          inputProps.forEach((property) => {
            const propertyName = `s${i}_${property}`;
            clonedValue[propertyName] = value[deCapitalizeFirstLetter(property)];
          });
          
          clonedValue.source = concatenatedSource;
          sessionGroupedData.push(clonedValue);
        } else {
          // Update an existing grouped item
          inputProps.forEach((property) => {
            const propertyName = `s${i}_${property}`;
            const existingItem = sessionGroupedData.find(c => c.sectionId === value.sectionId);
            if (existingItem) {
              existingItem[propertyName] = value[deCapitalizeFirstLetter(property)];
            }
          });
        }
      }
    }
  });
};

/**
 * Process single-section data
 */
export const processSingleSectionData = (
  designSessions: IDesignSession[], 
  sessionGroupedData: any[], 
  configurations: IUnitConfiguration[]
) => {
  if (designSessions.length === 0) return;
  
  const sectionNumber = 1;
  const inputProps = configurations
    .filter(c => c.sectionNumber === sectionNumber)
    .map(c => c.inputs)
    .flat();
  
  designSessions.forEach((value: any) => {
    const clonedValue = { ...value };
    
    inputProps.forEach((property) => {
      const propertyName = `s${sectionNumber}_${property}`;
      clonedValue[propertyName] = value[deCapitalizeFirstLetter(property)];
    });
    
    sessionGroupedData.push(clonedValue);
  });
};

/**
 * Set up columns based on unit configuration
 */
export const createSetColumnsFromConfig = (
  apiService: ApiService,
  state: BlindTestListState,
  onItemClick: (item: any) => void,
  theme: any,
  blindTests?: any[]
) => {
  return async () => {
    try {
      const response = await apiService.getAsync('settings/unitconfigurations/sections', state.unitInputConfiguration);
      if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
      }

      const configs: IUnitConfiguration[] = await response.json();
      logger.debug('Unit configurations loaded', {
        configsCount: configs.length,
        flowType: configs[0]?.flowType
      }, {
        component: 'BlindTestList',
        method: 'setColumnsFromConfig'
      });
      
      // Set preferred flowrate units based on the first config's flowType
      let currentFlowrateUnits = '';
      if (configs.length > 0 && configs[0].flowType) {
        currentFlowrateUnits = getFlowTypeUnits(configs[0].flowType);
        state.setPreferredFlowrateUnits(currentFlowrateUnits);
      }

      // Build a single format context for every dynamic cell in this table.
      // unitOperationTargetValueUnit lives on IUnitBaseConfiguration but the
      // API returns a richer object than IUnitConfiguration exposes, so we
      // read it via a loose cast to keep existing types unchanged.
      const firstConfig = configs[0] as (IUnitConfiguration & { unitOperationTargetValueUnit?: string }) | undefined;
      const formatContext: CellFormatContext = {
        flowType: firstConfig?.flowType,
        unitOperationTargetValueUnit: firstConfig?.unitOperationTargetValueUnit,
      };

      // Create base columns
      const baseColumns = createBaseColumns(onItemClick);
      
      // Add the media type column if not in blind tests mode
      if (!blindTests) {
        baseColumns.push({
          id: 'hotMediaType',
          label: 'Media Type',
          width: COLUMN_WIDTHS.mediaType,
          align: 'left',
          format: (value: any, row: any) => (
            <Tooltip title={row.hotMediaType || ''} arrow>
              <Typography variant="body2" noWrap>{row.hotMediaType}</Typography>
            </Tooltip>
          ),
          sortable: true
        });
        
        baseColumns.push({
          id: 'createdDate',
          label: 'Created',
          width: COLUMN_WIDTHS.date,
          align: 'left',
          format: (value: any, row: any) => <DateCell createdDate={row.createdDate} />,
          sortable: true
        });
      }

      // Generate dynamic columns based on configurations
      const dynamicColumns: ColumnConfig[] = [];

      for (let i = 0; i < configs.length; i++) {
        for (let j = 0; j < configs[i].inputs.length; j++) {
          const columnKey = deCapitalizeFirstLetter('s' + (i + 1).toString() + "_" + configs[i].inputs[j]);
          const columnName = `Sec${i + 1} ${getFormattedColumnValue(configs[i].inputs[j], configs[i])}`;
          
          dynamicColumns.push({
            id: columnKey,
            label: columnName,
            width: getColumnWidth(columnKey),
            align: 'left',
            format: (value: any, row: any) => {
              // Delegate to the shared formatter so we get proper metric<->US
              // conversion, unitOperationTargetValueUnit resolution and a
              // consistent '-' placeholder for missing values.
              const output = formatBlindTestCellValue(row[columnKey], columnKey, formatContext);

              return (
                <Tooltip title={output} arrow>
                  <Typography 
                    variant="body2" 
                    noWrap
                    sx={{ 
                      color: configs[i].inputs[j].includes('Hot') 
                        ? theme.palette.error.dark 
                        : theme.palette.primary.main 
                    }}
                  >
                    {output}
                  </Typography>
                </Tooltip>
              );
            },
            sortable: true
          });
        }
      }

      // Prepare all columns, starting with base and dynamic columns
      const allColumns = [...baseColumns, ...dynamicColumns];
      
      // Add the comment column at the very end if needed
      if (!blindTests) {
        allColumns.push({
          id: 'comment',
          label: 'Comment',
          width: COLUMN_WIDTHS.comment,
          align: 'left',
          format: (value: any, row: any) => row.modelerComment ? (
            <Tooltip title={row.modelerComment || ''} arrow>
              <ChatIcon fontSize="small" />
            </Tooltip>
          ) : null,
          sortable: false
        });
      }

      // Set both configurations and columns synchronously
      state.setConfigurations(configs);
      state.setColumns(allColumns);
    } catch (error) {
      logger.error('Failed to load configurations', error as Error, null, {
        component: 'BlindTestList',
        method: 'setColumnsFromConfig'
      });
      state.setIsLoading(false);
    }
  };
}; 